import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import type { TierInfo, UsageInfo, TierLevel, UsageEntry, FeatureAccessResult, TierLimits } from '@/types/index';

const TIER_ENFORCEMENT_ENABLED = import.meta.env.VITE_TIER_ENFORCEMENT_ENABLED === 'true';

const FREE_TIER_LIMITS: TierLimits = {
  kol: { searchesPerMonth: 3, resultsPerSearch: 10 },
  'medical-watch': { signalsPerWeek: 5, searchesPerMonth: 3, activeWindows: 1 },
  personas: { generationsPerMonth: 1 },
  'market-access': { searchesPerMonth: 3, decisionsPerSearch: 10 },
};

interface TierContextValue {
  tier: TierLevel;
  usage: UsageEntry[];
  limits: TierLimits;
  isFreeTier: boolean;
  isProTier: boolean;
  isEnterpriseTier: boolean;
  isLoading: boolean;
  upgradeUrl: string | undefined;
  enforcementEnabled: boolean;
  checkFeatureAccess: (product: string, feature: string) => FeatureAccessResult;
}

const TierContext = createContext<TierContextValue | null>(null);

export function TierProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const { data: tierData, isLoading: tierLoading } = useQuery({
    queryKey: ['tier', userId],
    queryFn: () => api.get<TierInfo>(`/api/tier/${userId}`).then((r) => r.data),
    enabled: !!userId && TIER_ENFORCEMENT_ENABLED,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: usageData, isLoading: usageLoading } = useQuery({
    queryKey: ['usage', userId],
    queryFn: () => api.get<UsageInfo>(`/api/usage/${userId}`).then((r) => r.data),
    enabled: !!userId && TIER_ENFORCEMENT_ENABLED,
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  // Refresh tier/usage data when window regains focus
  useEffect(() => {
    if (!userId || !TIER_ENFORCEMENT_ENABLED) return;
    const onFocus = () => {
      void queryClient.invalidateQueries({ queryKey: ['tier', userId] });
      void queryClient.invalidateQueries({ queryKey: ['usage', userId] });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [userId, queryClient]);

  const tier: TierLevel = TIER_ENFORCEMENT_ENABLED ? (tierData?.tier ?? 'free') : 'pro';
  const usage: UsageEntry[] = usageData?.usage ?? [];
  const upgradeUrl = tierData?.upgradeUrl;

  const checkFeatureAccess = useCallback(
    (product: string, feature: string): FeatureAccessResult => {
      // When enforcement is off, everything is allowed
      if (!TIER_ENFORCEMENT_ENABLED) return { allowed: true };

      // Pro and enterprise have full access
      if (tier !== 'free') return { allowed: true };

      // Check volume limits
      const usageEntry = usage.find((u) => u.product === product && u.action === feature);
      if (usageEntry) {
        return {
          allowed: usageEntry.current < usageEntry.limit,
          current: usageEntry.current,
          limit: usageEntry.limit,
        };
      }

      // Check feature-level blocks (pro-only features)
      const proOnlyFeatures: Record<string, string[]> = {
        kol: ['rising-stars', 'key-followers'],
        'medical-watch': ['custom-alerts', 'export', 'multi-ta'],
        personas: ['interview', 'simulate', 'batch'],
        'market-access': ['cross-country', 'stakeholders', 'alerts', 'export'],
      };

      const blocked = proOnlyFeatures[product]?.includes(feature);
      if (blocked) {
        return { allowed: false };
      }

      return { allowed: true };
    },
    [tier, usage],
  );

  const value = useMemo<TierContextValue>(
    () => ({
      tier,
      usage,
      limits: FREE_TIER_LIMITS,
      isFreeTier: tier === 'free',
      isProTier: tier === 'pro',
      isEnterpriseTier: tier === 'enterprise',
      isLoading: tierLoading || usageLoading,
      upgradeUrl,
      enforcementEnabled: TIER_ENFORCEMENT_ENABLED,
      checkFeatureAccess,
    }),
    [tier, usage, tierLoading, usageLoading, upgradeUrl, checkFeatureAccess],
  );

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>;
}

export function useTier() {
  const context = useContext(TierContext);
  if (!context) throw new Error('useTier must be used within TierProvider');
  return context;
}
